import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Plus, CheckCircle, ShieldCheck, KeyRound, Wrench, Sparkles, Image as ImageIcon, Video, Star } from "lucide-react";
import UploadButton from "@/components/UploadButton";
import MultiUploadButton from "@/components/MultiUploadButton";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin" && user.role !== "trustee") return <Navigate to="/" replace />;
  const isAdmin = user.role === "admin";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-8 w-8 text-[#5C061C]" />
        <h1 className="font-serif text-4xl text-[#5C061C]">{t("dashboard.title")}</h1>
        <Badge className="bg-[#5C061C]/10 text-[#5C061C]">{t(`roles.${user.role}`)}</Badge>
      </div>

      <Tabs defaultValue="users" className="mt-8">
        <TabsList className="bg-[#F4EEDF] flex flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="tab-users">{t("dashboard.users")}</TabsTrigger>
          <TabsTrigger value="books" data-testid="tab-books">{t("dashboard.books")}</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">{t("dashboard.products")}</TabsTrigger>
          <TabsTrigger value="saadhnas" data-testid="tab-saadhnas">Saadhnas</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">{t("dashboard.events")}</TabsTrigger>
          <TabsTrigger value="leaders" data-testid="tab-leaders">Leaders</TabsTrigger>
          <TabsTrigger value="posts" data-testid="tab-posts">Daily Posts</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">{t("dashboard.orders")}</TabsTrigger>
          <TabsTrigger value="rentals" data-testid="tab-rentals">{t("dashboard.rentals")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="coupons" data-testid="tab-coupons">{t("dashboard.coupons")}</TabsTrigger>}
          <TabsTrigger value="payments" data-testid="tab-payments">{t("dashboard.payments")}</TabsTrigger>
          <TabsTrigger value="site" data-testid="tab-site">Site Control</TabsTrigger>
          <TabsTrigger value="pwreset" data-testid="tab-pwreset">Password Reset</TabsTrigger>
        </TabsList>

        <TabsContent value="users"><UsersTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="books"><BooksTab /></TabsContent>
        <TabsContent value="products"><ProductsTab /></TabsContent>
        <TabsContent value="saadhnas"><SaadhnasTab /></TabsContent>
        <TabsContent value="events"><EventsTab /></TabsContent>
        <TabsContent value="leaders"><LeadersTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="posts"><PostsTab /></TabsContent>
        <TabsContent value="orders"><OrdersTab /></TabsContent>
        <TabsContent value="rentals"><RentalsTab /></TabsContent>
        {isAdmin && <TabsContent value="coupons"><CouponsTab /></TabsContent>}
        <TabsContent value="payments"><PaymentsTab /></TabsContent>
        <TabsContent value="site"><SiteControlTab /></TabsContent>
        <TabsContent value="pwreset"><PwResetTab isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ children }) {
  return <div className="card-warm rounded-2xl p-5 mt-6">{children}</div>;
}

function UsersTab({ isAdmin }) {
  const [users, setUsers] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);
  const roles = ["member", "approved_member", "trustee", "admin"];
  const setRole = async (id, role) => { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); };
  const approveCore = async (id) => { await api.put(`/admin/users/${id}/approve`); toast.success("Approved as Core Member"); load(); };
  const activate = async (id) => { await api.put(`/admin/users/${id}/activate`); toast.success("User activated"); load(); };
  const reject = async (id) => { if (!window.confirm("Reject this user? They will not be able to log in.")) return; await api.put(`/admin/users/${id}/reject`); toast.success("User rejected"); load(); };
  const deactivate = async (id) => { if (!window.confirm("Deactivate this user? They will not be able to log in until reactivated.")) return; await api.put(`/admin/users/${id}/deactivate`); toast.success("User deactivated"); load(); };

  const pending = users.filter((u) => (u.status || "active") === "pending");
  const active = users.filter((u) => (u.status || "active") !== "pending");

  return (
    <div className="space-y-6 mt-6">
      {pending.length > 0 && (
        <div className="card-warm rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-[#D97706]/10 text-[#B45309]">Pending Registrations · {pending.length}</Badge>
            <p className="text-xs text-[#6E6259]">Review new devotees and approve or reject.</p>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Mobile</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Submitted</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {pending.map((u) => (
                <TableRow key={u.id} data-testid={`pending-row-${u.id}`}>
                  <TableCell className="font-mono">{u.mobile}</TableCell>
                  <TableCell>{u.name || "—"}</TableCell>
                  <TableCell className="text-xs">{u.email || "—"}</TableCell>
                  <TableCell className="text-xs">{u.created_at ? new Date(u.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => activate(u.id)} className="btn-saffron h-8" data-testid={`activate-user-${u.id}`}><CheckCircle className="h-3 w-3 mr-1" />Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => reject(u.id)} className="h-8 border-[#DC2626] text-[#DC2626]" data-testid={`reject-user-${u.id}`}>Reject</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="card-warm rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-[#5C061C]">All members · {active.length}</p>
          <label className="text-xs text-[#6E6259] flex items-center gap-2"><Switch checked={showAll} onCheckedChange={setShowAll} /> Include rejected & inactive</label>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Mobile</TableHead><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Core</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {active.filter((u) => showAll || (u.status || "active") !== "rejected").map((u) => {
              const status = u.status || "active";
              const isSelf = false; // no way to know without user context; kept simple
              const isAdminRow = u.role === "admin";
              return (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-mono">{u.mobile}</TableCell>
                <TableCell>{u.name || "—"}</TableCell>
                <TableCell><Badge className="bg-[#5C061C]/10 text-[#5C061C]">{u.role}</Badge></TableCell>
                <TableCell><Badge className={`${status === "active" ? "bg-[#15803D]/10 text-[#15803D]" : status === "inactive" ? "bg-[#B45309]/10 text-[#B45309]" : "bg-[#DC2626]/10 text-[#DC2626]"}`}>{status}</Badge></TableCell>
                <TableCell>{u.is_approved_member ? <Badge className="bg-[#15803D]/10 text-[#15803D]">Core</Badge> : "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-2 flex-wrap">
                    {isAdmin && (
                      <>
                        <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                        {!u.is_approved_member && (
                          <Button size="sm" onClick={() => approveCore(u.id)} className="btn-saffron h-8" data-testid={`approve-core-${u.id}`}><CheckCircle className="h-3 w-3 mr-1" />Core</Button>
                        )}
                      </>
                    )}
                    {!isAdminRow && status === "active" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => deactivate(u.id)} className="h-8 border-[#B45309] text-[#B45309]" data-testid={`deactivate-user-${u.id}`}>Deactivate</Button>
                        <Button size="sm" variant="outline" onClick={() => reject(u.id)} className="h-8 border-[#DC2626] text-[#DC2626]" data-testid={`reject-user-${u.id}`}>Reject</Button>
                      </>
                    )}
                    {!isAdminRow && (status === "inactive" || status === "rejected") && (
                      <Button size="sm" onClick={() => activate(u.id)} className="btn-saffron h-8" data-testid={`activate-user-${u.id}`}><CheckCircle className="h-3 w-3 mr-1" />Reactivate</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function BooksTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = { title: "", author: "", description: "", cover_url: "", pdf_url: "", language: "en", durations: [{ id: Math.random().toString(36).slice(2), label: "7 Days", days: 7, price: 49 }] };
  const [form, setForm] = useState(emptyForm);
  const load = () => api.get("/books").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/admin/books", form); toast.success("Book created"); setOpen(false); load(); setForm(emptyForm); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/admin/books/${id}`); toast.success("Retired"); load(); };

  return (
    <Card>
      <Button className="btn-saffron rounded-full mb-4" onClick={() => setOpen(true)} data-testid="add-book-button"><Plus className="h-4 w-4 mr-1" /> Add Book</Button>
      <Table>
        <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Author</TableHead><TableHead>Lang</TableHead><TableHead>PDF</TableHead><TableHead>Durations</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((b) => (
            <TableRow key={b.id}>
              <TableCell>{b.title}</TableCell>
              <TableCell>{b.author}</TableCell>
              <TableCell>{b.language}</TableCell>
              <TableCell>{b.pdf_url ? <Badge className="bg-[#15803D]/10 text-[#15803D]">PDF</Badge> : <Badge className="bg-[#D97706]/10 text-[#B45309]">No PDF</Badge>}</TableCell>
              <TableCell className="text-xs">{b.durations?.map(d => `${d.label} ₹${d.price}`).join(" · ")}</TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => remove(b.id)} data-testid={`retire-book-${b.id}`}><Trash2 className="h-4 w-4 text-[#DC2626]" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#5C061C]">New Book</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="book-title-input" />
            <Input placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="book-author-input" />
            <div className="flex gap-2 items-center">
              <Input placeholder="Cover image URL" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
              <UploadButton accept="image/*" onUploaded={(url) => setForm((f) => ({ ...f, cover_url: url }))} testid="book-cover-upload" label="Upload" />
            </div>
            <div className="flex gap-2 items-center">
              <Input placeholder="PDF URL (sacred text)" value={form.pdf_url} onChange={(e) => setForm({ ...form, pdf_url: e.target.value })} data-testid="book-pdf-input" />
              <UploadButton accept="application/pdf,.pdf" onUploaded={(url) => setForm((f) => ({ ...f, pdf_url: url }))} testid="book-pdf-upload" label="Upload PDF" />
            </div>
            {form.pdf_url && <p className="text-xs text-[#15803D]">✓ PDF attached</p>}
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="hi">Hindi</SelectItem><SelectItem value="mr">Marathi</SelectItem></SelectContent>
            </Select>
            <Label>Durations</Label>
            {form.durations.map((d, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <Input placeholder="Label" value={d.label} onChange={(e) => { const c = [...form.durations]; c[i].label = e.target.value; setForm({ ...form, durations: c }); }} />
                <Input type="number" placeholder="Days" value={d.days} onChange={(e) => { const c = [...form.durations]; c[i].days = Number(e.target.value); setForm({ ...form, durations: c }); }} />
                <Input type="number" placeholder="Price ₹" value={d.price} onChange={(e) => { const c = [...form.durations]; c[i].price = Number(e.target.value); setForm({ ...form, durations: c }); }} />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setForm({ ...form, durations: [...form.durations, { id: Math.random().toString(36).slice(2), label: "", days: 30, price: 99 }] })}>+ Add duration</Button>
            <Button onClick={save} className="btn-saffron w-full rounded-full h-11" data-testid="book-save-button">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ProductsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: 0, stock: 0, image_url: "", category: "spiritual" });
  const load = () => api.get("/products").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/admin/products", form); toast.success("Created"); setOpen(false); load(); setForm({ name: "", description: "", price: 0, stock: 0, image_url: "", category: "spiritual" }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const remove = async (id) => { await api.delete(`/admin/products/${id}`); load(); };
  return (
    <Card>
      <Button className="btn-saffron rounded-full mb-4" onClick={() => setOpen(true)} data-testid="add-product-button"><Plus className="h-4 w-4 mr-1" /> Add Product</Button>
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.id}><TableCell>{p.name}</TableCell><TableCell>₹{p.price}</TableCell><TableCell>{p.stock}</TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4 text-[#DC2626]" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#5C061C]">New Product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" />
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="flex gap-2 items-center">
              <Input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              <UploadButton accept="image/*" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} testid="product-image-upload" label="Upload" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Price ₹" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} data-testid="product-price-input" />
              <Input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} data-testid="product-stock-input" />
            </div>
            <Button onClick={save} className="btn-saffron w-full rounded-full h-11" data-testid="product-save-button">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function EventsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", image_url: "", is_live: true });
  const [mediaFor, setMediaFor] = useState(null);
  const [mForm, setMForm] = useState({ kind: "image", url: "", caption: "" });
  const load = () => api.get("/events").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/admin/events", form); toast.success("Created"); setOpen(false); load(); setForm({ title: "", description: "", image_url: "", is_live: true }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const toggleLive = async (e) => { await api.put(`/admin/events/${e.id}`, { ...e, is_live: !e.is_live }); load(); };
  const remove = async (id) => { await api.delete(`/admin/events/${id}`); load(); };
  const addMedia = async () => {
    if (!mForm.url) return toast.error("Enter a URL");
    await api.post(`/admin/events/${mediaFor.id}/media`, mForm);
    toast.success("Media added");
    setMForm({ kind: "image", url: "", caption: "" });
    const { data } = await api.get(`/events/${mediaFor.id}`);
    setMediaFor(data);
    load();
  };
  const removeMedia = async (mid) => {
    await api.delete(`/admin/events/${mediaFor.id}/media/${mid}`);
    const { data } = await api.get(`/events/${mediaFor.id}`);
    setMediaFor(data);
    load();
  };
  const setCover = async (mid) => {
    try {
      await api.put(`/admin/events/${mediaFor.id}/media/${mid}/cover`);
      toast.success("Cover updated");
      const { data } = await api.get(`/events/${mediaFor.id}`);
      setMediaFor(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Card>
      <Button className="btn-saffron rounded-full mb-4" onClick={() => setOpen(true)} data-testid="add-event-button"><Plus className="h-4 w-4 mr-1" /> Add Event</Button>
      <Table>
        <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Live</TableHead><TableHead>Media</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((e) => (
            <TableRow key={e.id}>
              <TableCell>{e.title}</TableCell>
              <TableCell><Switch checked={e.is_live} onCheckedChange={() => toggleLive(e)} data-testid={`event-live-toggle-${e.id}`} /></TableCell>
              <TableCell>
                <Button size="sm" variant="outline" onClick={() => setMediaFor(e)} data-testid={`event-media-${e.id}`}>
                  <ImageIcon className="h-3 w-3 mr-1" /> {(e.media || []).length}
                </Button>
              </TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => remove(e.id)}><Trash2 className="h-4 w-4 text-[#DC2626]" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#5C061C]">New Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" />
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="flex gap-2 items-center">
              <Input placeholder="Cover Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              <UploadButton accept="image/*" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} testid="event-cover-upload" label="Upload" />
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.is_live} onCheckedChange={(v) => setForm({ ...form, is_live: v })} /><span>LIVE</span></div>
            <Button onClick={save} className="btn-saffron w-full rounded-full h-11" data-testid="event-save-button">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!mediaFor} onOpenChange={(o) => { if (!o) setMediaFor(null); }}>
        <DialogContent className="bg-[#FAF7F2] max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#5C061C]">Gallery · {mediaFor?.title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Select value={mForm.kind} onValueChange={(v) => setMForm({ ...mForm, kind: v })}>
              <SelectTrigger data-testid="media-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="pdf">PDF Document</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Caption (optional)" value={mForm.caption} onChange={(e) => setMForm({ ...mForm, caption: e.target.value })} data-testid="media-caption-input" />
          </div>
          <Input placeholder="Media URL (image, video or PDF)" value={mForm.url} onChange={(e) => setMForm({ ...mForm, url: e.target.value })} data-testid="media-url-input" />
          <div className="flex gap-2">
            <UploadButton accept="image/*,video/*,application/pdf" onUploaded={(url, meta) => setMForm({ ...mForm, url, kind: meta.kind === "video" ? "video" : (meta.content_type === "application/pdf" ? "pdf" : "image") })} testid="media-upload" label="Upload photo, video or PDF" />
            <Button onClick={addMedia} className="btn-saffron rounded-full h-11" data-testid="media-add-button"><Plus className="h-4 w-4 mr-1" /> Add to gallery</Button>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 max-h-72 overflow-y-auto">
            {(mediaFor?.media || []).map((m) => {
              const isCover = mediaFor?.cover_media_id === m.id || mediaFor?.image_url === m.url;
              return (
                <div key={m.id} className={`relative aspect-square rounded overflow-hidden bg-black ${isCover ? "ring-2 ring-[#D97706]" : ""}`}>
                  {m.kind === "image" ? (
                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                  ) : m.kind === "video" ? (
                    <video src={m.url} className="w-full h-full object-cover" />
                  ) : (
                    <a href={m.url.startsWith("http") ? m.url : `${process.env.REACT_APP_BACKEND_URL}${m.url}`} target="_blank" rel="noopener noreferrer" className="w-full h-full grid place-items-center bg-gradient-to-br from-[#F4EEDF] to-[#F9E4B7] text-[#5C061C] text-xs font-semibold">📄 PDF</a>
                  )}
                  <button onClick={() => removeMedia(m.id)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 grid place-items-center" data-testid={`media-remove-${m.id}`}>
                    <Trash2 className="h-3 w-3 text-[#DC2626]" />
                  </button>
                  {m.kind === "image" && (
                    <button onClick={() => setCover(m.id)} className={`absolute top-1 left-1 h-6 px-1.5 rounded-full grid place-items-center gap-1 text-[10px] font-semibold ${isCover ? "bg-[#D97706] text-white" : "bg-white/90 text-[#5C061C]"}`} data-testid={`media-cover-${m.id}`}>
                      <Star className="h-3 w-3" /> {isCover ? "Cover" : "Set cover"}
                    </button>
                  )}
                  <span className="absolute bottom-1 left-1 h-5 w-5 rounded bg-white/90 grid place-items-center">{m.kind === "video" ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}</span>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SaadhnasTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { deity: "", title: "", description: "", image_url: "", price: 501, duration_days: 30, mantra: "", daily_routine: "", guidance_content: "", active: true };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/admin/saadhnas").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/admin/saadhnas", form);
      toast.success("Saadhna created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (s) => { await api.put(`/admin/saadhnas/${s.id}`, { ...s, active: !s.active }); load(); };
  const remove = async (id) => { await api.delete(`/admin/saadhnas/${id}`); load(); };
  return (
    <Card>
      <Button className="btn-saffron rounded-full mb-4" onClick={() => setOpen(true)} data-testid="add-saadhna-button"><Plus className="h-4 w-4 mr-1" /> Add Saadhna</Button>
      <Table>
        <TableHeader><TableRow><TableHead>Deity</TableHead><TableHead>Title</TableHead><TableHead>Price</TableHead><TableHead>Days</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((s) => (
            <TableRow key={s.id} data-testid={`saadhna-row-${s.id}`}>
              <TableCell>{s.deity}</TableCell>
              <TableCell>{s.title}</TableCell>
              <TableCell>₹{s.price}</TableCell>
              <TableCell>{s.duration_days}</TableCell>
              <TableCell><Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`saadhna-toggle-${s.id}`} /></TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => remove(s.id)}><Trash2 className="h-4 w-4 text-[#DC2626]" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#5C061C]">New Saadhna</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid sm:grid-cols-2 gap-2">
              <Input placeholder="Deity (e.g. Devi Lalita)" value={form.deity} onChange={(e) => setForm({ ...form, deity: e.target.value })} data-testid="saadhna-deity-input" />
              <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="saadhna-title-input" />
            </div>
            <Textarea placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="flex gap-2 items-center">
              <Input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              <UploadButton accept="image/*" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} testid="saadhna-image-upload" label="Upload" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input type="number" placeholder="Price ₹" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} data-testid="saadhna-price-input" />
              <Input type="number" placeholder="Duration days" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) })} />
            </div>
            <Textarea placeholder="Mantra" value={form.mantra} onChange={(e) => setForm({ ...form, mantra: e.target.value })} />
            <Textarea placeholder="Daily routine" value={form.daily_routine} onChange={(e) => setForm({ ...form, daily_routine: e.target.value })} />
            <Textarea placeholder="Guidance content (shown to enrolled devotees in profile)" rows={6} value={form.guidance_content} onChange={(e) => setForm({ ...form, guidance_content: e.target.value })} />
            <Button onClick={save} className="btn-saffron w-full rounded-full h-11" data-testid="saadhna-save-button">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SiteControlTab() {
  const [cfg, setCfg] = useState(null);
  const load = () => api.get("/site-config").then(({ data }) => setCfg(data || {}));
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    const { data } = await api.put("/staff/site-config", patch);
    setCfg(data);
    toast.success("Saved");
  };
  const togglePage = async (key) => {
    const toggles = { ...(cfg.page_toggles || {}), [key]: !(cfg.page_toggles?.[key] !== false) };
    await save({ page_toggles: toggles });
  };
  if (!cfg) return <Card><p className="text-[#6E6259]">Loading...</p></Card>;
  const pages = [
    { key: "books", label: "Books" },
    { key: "shop", label: "Shop" },
    { key: "saadhna", label: "Saadhna" },
    { key: "events", label: "Events" },
    { key: "about", label: "About" },
    { key: "contact", label: "Contact" },
  ];
  return (
    <Card>
      <div className="rounded-xl border border-[#E8DEC8] bg-white p-5 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-[#5C061C]" />
              <p className="font-serif text-lg text-[#5C061C]">Site maintenance mode</p>
            </div>
            <p className="text-xs text-[#6E6259] mt-1">When ON, devotees see a maintenance screen. Trustees & Admin still have full access.</p>
          </div>
          <Switch checked={!!cfg.maintenance_mode} onCheckedChange={(v) => save({ maintenance_mode: v })} data-testid="maintenance-toggle" />
        </div>
        <Textarea className="mt-3" placeholder="Maintenance message" value={cfg.maintenance_message || ""} onChange={(e) => setCfg({ ...cfg, maintenance_message: e.target.value })} onBlur={() => save({ maintenance_message: cfg.maintenance_message })} data-testid="maintenance-message" />
      </div>

      <div className="rounded-xl border border-[#E8DEC8] bg-white p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-[#5C061C]" />
          <p className="font-serif text-lg text-[#5C061C]">Site-wide announcement banner</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="e.g. Navaratri celebrations begin this Sunday!" value={cfg.announcement || ""} onChange={(e) => setCfg({ ...cfg, announcement: e.target.value })} data-testid="announcement-input" />
          <Button onClick={() => save({ announcement: cfg.announcement })} className="btn-saffron rounded-full" data-testid="announcement-save">Save</Button>
        </div>
      </div>

      <div className="rounded-xl border border-[#E8DEC8] bg-white p-5">
        <p className="font-serif text-lg text-[#5C061C] mb-2">Individual page toggles</p>
        <p className="text-xs text-[#6E6259] mb-3">Turn off a page to hide it from devotees (staff can still open it).</p>
        <div className="grid sm:grid-cols-3 gap-2">
          {pages.map((p) => {
            const on = cfg.page_toggles?.[p.key] !== false;
            return (
              <label key={p.key} className="flex items-center justify-between rounded-lg border border-[#E8DEC8] bg-white p-3">
                <span className="text-sm font-medium text-[#5C061C]">{p.label}</span>
                <Switch checked={on} onCheckedChange={() => togglePage(p.key)} data-testid={`page-toggle-${p.key}`} />
              </label>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function PwResetTab({ isAdmin }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [temp, setTemp] = useState(null);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data)); }, []);
  const reset = async (u) => {
    if (u.role === "admin" && !isAdmin) { toast.error("Only an admin can reset another admin"); return; }
    if (!window.confirm(`Reset password for ${u.mobile}?`)) return;
    try {
      const { data } = await api.post("/staff/reset-user-password", { user_id: u.id });
      setTemp(data);
      toast.success("Password reset");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const filtered = users.filter((u) => !q || u.mobile.includes(q) || (u.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="h-5 w-5 text-[#5C061C]" />
        <p className="font-serif text-lg text-[#5C061C]">Password reset (admin & members)</p>
      </div>
      <Input placeholder="Search by mobile or name" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" data-testid="pwreset-search" />
      {temp && (
        <div className="rounded-lg border border-[#D97706] bg-[#FEF3C7] p-4 mb-4" data-testid="pwreset-result">
          <p className="text-xs uppercase tracking-widest text-[#B45309]">Temporary password for {temp.mobile}</p>
          <p className="font-mono text-lg text-[#5C061C] mt-1 select-all">{temp.temporary_password}</p>
          <p className="text-xs text-[#6E6259] mt-1">Share this once; ask the devotee to change it on next login.</p>
          <Button variant="ghost" size="sm" onClick={() => setTemp(null)} className="mt-2">Dismiss</Button>
        </div>
      )}
      <Table>
        <TableHeader><TableRow><TableHead>Mobile</TableHead><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Approved</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {filtered.slice(0, 100).map((u) => (
            <TableRow key={u.id}>
              <TableCell className="font-mono">{u.mobile}</TableCell>
              <TableCell>{u.name || "—"}</TableCell>
              <TableCell><Badge className="bg-[#5C061C]/10 text-[#5C061C]">{u.role}</Badge></TableCell>
              <TableCell>{u.is_approved_member ? "Core" : "—"}</TableCell>
              <TableCell>
                <Button size="sm" onClick={() => reset(u)} className="btn-saffron h-8" data-testid={`pwreset-${u.id}`}>
                  <KeyRound className="h-3 w-3 mr-1" /> Reset
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function OrdersTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/orders").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const collect = async (id) => { await api.put(`/admin/orders/${id}/collected`); toast.success("Marked collected"); load(); };
  return (
    <Card>
      <Table>
        <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Pickup</TableHead><TableHead>Code</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((o) => (
            <TableRow key={o.id}>
              <TableCell>{o.user_mobile}</TableCell>
              <TableCell className="text-xs">{o.items.map(i => i.name).join(", ")}</TableCell>
              <TableCell>₹{o.total}</TableCell>
              <TableCell>{o.product_pickup_status ? <Badge className={o.product_pickup_status === "collected" ? "bg-[#15803D]/10 text-[#15803D]" : "bg-[#D97706]/10 text-[#B45309]"}>{o.product_pickup_status}</Badge> : "—"}</TableCell>
              <TableCell className="font-mono text-xs">{o.pickup_code || "—"}</TableCell>
              <TableCell>{o.product_pickup_status === "ready_for_pickup" && <Button size="sm" onClick={() => collect(o.id)} className="btn-saffron h-8" data-testid={`collect-${o.id}`}>Collected</Button>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function RentalsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/rentals").then(({ data }) => setItems(data)); }, []);
  return (
    <Card>
      <Table>
        <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Book</TableHead><TableHead>Days</TableHead><TableHead>Expires</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((r) => (
            <TableRow key={r.id}><TableCell>{r.user_mobile}</TableCell><TableCell>{r.book_title}</TableCell><TableCell>{r.days}</TableCell><TableCell>{new Date(r.expires_at).toLocaleString()}</TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function CouponsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", percent: 10, approved_only: false, active: true });
  const load = () => api.get("/admin/coupons").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/admin/coupons", form); toast.success("Created"); load(); setForm({ code: "", percent: 10, approved_only: false, active: true }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const remove = async (id) => { await api.delete(`/admin/coupons/${id}`); load(); };
  return (
    <Card>
      <div className="grid sm:grid-cols-5 gap-2 mb-4">
        <Input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code-input" />
        <Input type="number" placeholder="Percent" value={form.percent} onChange={(e) => setForm({ ...form, percent: Number(e.target.value) })} data-testid="coupon-percent-input" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.approved_only} onCheckedChange={(v) => setForm({ ...form, approved_only: v })} /> Core only</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active</label>
        <Button onClick={save} className="btn-saffron rounded-full" data-testid="coupon-save-button"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>%</TableHead><TableHead>Core</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id}><TableCell className="font-mono">{c.code}</TableCell><TableCell>{c.percent}%</TableCell><TableCell>{c.approved_only ? "Yes" : "—"}</TableCell><TableCell>{c.active ? "Yes" : "—"}</TableCell><TableCell><Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4 text-[#DC2626]" /></Button></TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function LeadersTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { name: "", role: "", description: "", photo_url: "", contact: "" };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/leaders").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!isAdmin) { toast.error("Only admin can add leaders"); return; }
    try { await api.post("/admin/leaders", form); toast.success("Leader added"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!isAdmin) { toast.error("Only admin can remove"); return; }
    await api.delete(`/admin/leaders/${id}`); load();
  };
  return (
    <Card>
      {isAdmin && <Button className="btn-saffron rounded-full mb-4" onClick={() => setOpen(true)} data-testid="add-leader-button"><Plus className="h-4 w-4 mr-1" /> Add Leader</Button>}
      {!isAdmin && <p className="text-xs text-[#6E6259] mb-3">Trustees can view; only admin can add or remove leaders.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((l) => (
          <div key={l.id} className="card-warm rounded-2xl overflow-hidden" data-testid={`leader-row-${l.id}`}>
            <div className="aspect-square bg-[#F4EEDF]">
              {l.photo_url && <img src={l.photo_url} alt={l.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <p className="font-serif text-lg text-[#5C061C]">{l.name}</p>
              <p className="text-xs uppercase tracking-widest text-[#B45309] mt-1">{l.role}</p>
              <p className="text-xs text-[#6E6259] mt-2 line-clamp-3">{l.description}</p>
              {l.contact && <p className="text-xs text-[#5C061C] mt-2">{l.contact}</p>}
              {isAdmin && <Button size="sm" variant="ghost" onClick={() => remove(l.id)} className="mt-2" data-testid={`leader-remove-${l.id}`}><Trash2 className="h-3 w-3 text-[#DC2626]" /></Button>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#5C061C]">New Leader</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="leader-name-input" />
            <Input placeholder="Role (e.g. President, Trustee)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="leader-role-input" />
            <div className="flex gap-2 items-center">
              <Input placeholder="Photo URL" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} />
              <UploadButton accept="image/*" onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))} testid="leader-photo-upload" label="Upload" />
            </div>
            {form.photo_url && <img src={form.photo_url} alt="" className="h-24 w-24 rounded-lg object-cover" />}
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Input placeholder="Contact (phone/email)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="leader-contact-input" />
            <Button onClick={save} className="btn-saffron w-full rounded-full h-11" data-testid="leader-save-button">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PostsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { id: null, title: "", description: "", image_url: "", date: "", pinned: false, media: [], cta_label: "", cta_url: "" };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/posts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      id: p.id,
      title: p.title || "",
      description: p.description || "",
      image_url: p.image_url || "",
      date: p.date ? p.date.slice(0, 10) : "",
      pinned: !!p.pinned,
      media: p.media || [],
      cta_label: p.cta_label || "",
      cta_url: p.cta_url || "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const payload = {
      title: form.title.trim(),
      description: form.description,
      image_url: form.image_url,
      date: form.date ? new Date(form.date).toISOString() : null,
      pinned: !!form.pinned,
      media: form.media || [],
      cta_label: form.cta_label || "",
      cta_url: form.cta_url || "",
    };
    try {
      if (form.id) await api.put(`/admin/posts/${form.id}`, payload);
      else await api.post("/admin/posts", payload);
      toast.success(form.id ? "Post updated" : "Post published");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const togglePin = async (p) => {
    try {
      await api.put(`/admin/posts/${p.id}`, {
        title: p.title,
        description: p.description || "",
        image_url: p.image_url || "",
        date: p.date || null,
        pinned: !p.pinned,
        media: p.media || [],
        cta_label: p.cta_label || "",
        cta_url: p.cta_url || "",
      });
      toast.success(!p.pinned ? "Pinned to top" : "Unpinned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/posts/${id}`); toast.success("Deleted"); load();
  };
  const addMedia = (items) => setForm((f) => ({ ...f, media: [...(f.media || []), ...items] }));
  const removeMedia = (idx) => setForm((f) => ({ ...f, media: (f.media || []).filter((_, i) => i !== idx) }));
  const updateCaption = (idx, caption) => setForm((f) => ({ ...f, media: (f.media || []).map((m, i) => i === idx ? { ...m, caption } : m) }));
  const resolveMediaUrl = (u) => u && !u.startsWith("http") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#6E6259]">Publish daily updates. Attach photos, videos or PDFs — all optional.</p>
        <Button className="btn-saffron rounded-full" onClick={openNew} data-testid="add-post-button"><Plus className="h-4 w-4 mr-1" /> New Post</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => {
          const mediaCount = (p.media || []).length + (p.image_url ? 1 : 0);
          const cover = p.image_url || (p.media || []).find((m) => m.kind === "image")?.url;
          return (
            <div key={p.id} className="card-warm rounded-2xl overflow-hidden" data-testid={`post-row-${p.id}`}>
              {cover && (
                <div className="aspect-[16/10] bg-[#F4EEDF] relative">
                  <img src={resolveMediaUrl(cover)} alt={p.title} className="w-full h-full object-cover" />
                  {mediaCount > 1 && <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">{mediaCount} media</span>}
                </div>
              )}
              <div className="p-4">
                <p className="text-xs uppercase tracking-widest text-[#B45309]">{new Date(p.date || p.created_at).toLocaleDateString()}</p>
                <div className="flex items-center gap-2 mt-1">
                  {p.pinned && <Badge className="bg-[#D97706] text-white text-[10px]">📌 Pinned</Badge>}
                  <p className="font-serif text-lg text-[#5C061C] line-clamp-2">{p.title}</p>
                </div>
                <p className="text-xs text-[#6E6259] mt-2 line-clamp-3 whitespace-pre-wrap">{p.description}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button size="sm" variant={p.pinned ? "default" : "outline"} onClick={() => togglePin(p)} data-testid={`post-pin-${p.id}`} className={p.pinned ? "bg-[#D97706] hover:bg-[#B45309]" : ""}>{p.pinned ? "Unpin" : "Pin"}</Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`post-edit-${p.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`post-remove-${p.id}`}><Trash2 className="h-3 w-3 text-[#DC2626]" /></Button>
                </div>
              </div>
            </div>
          );
        })}
        {!items.length && <p className="text-sm text-[#6E6259] col-span-full">No posts yet. Publish the first update to appear on the Home page.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#5C061C]">{form.id ? "Edit Post" : "New Post"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title-input" />
            <Textarea placeholder="Description (optional)" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="post-description-input" />
            <div>
              <Label className="text-xs text-[#6E6259]">Cover image (optional)</Label>
              <div className="flex gap-2 items-center mt-1">
                <Input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
                <UploadButton accept="image/*" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} testid="post-image-upload" label="Upload" />
              </div>
              {form.image_url && <img src={resolveMediaUrl(form.image_url)} alt="" className="mt-2 h-32 w-full object-cover rounded-lg" />}
            </div>
            <div className="rounded-lg border border-[#E8DEC8] p-3 bg-white/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm text-[#5C061C]">Attachments (optional)</Label>
                <MultiUploadButton onUploaded={addMedia} testid="post-media-upload" label="Add photos, videos, PDFs" />
              </div>
              <p className="text-xs text-[#6E6259] mb-2">Add as many images, videos or PDF documents as you like.</p>
              {(form.media || []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {form.media.map((m, idx) => (
                    <div key={idx} className="relative rounded-lg overflow-hidden bg-black/5 aspect-square" data-testid={`post-media-item-${idx}`}>
                      {m.kind === "image" && <img src={resolveMediaUrl(m.url)} alt="" className="w-full h-full object-cover" />}
                      {m.kind === "video" && <video src={resolveMediaUrl(m.url)} className="w-full h-full object-cover" muted />}
                      {m.kind === "pdf" && (
                        <div className="w-full h-full bg-gradient-to-br from-[#F4EEDF] to-[#F9E4B7] grid place-items-center p-2">
                          <span className="text-[10px] text-[#5C061C] font-semibold text-center">📄 PDF</span>
                        </div>
                      )}
                      <button type="button" onClick={() => removeMedia(idx)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 grid place-items-center" data-testid={`post-media-remove-${idx}`}>
                        <Trash2 className="h-3 w-3 text-[#DC2626]" />
                      </button>
                      <Input placeholder="Caption" value={m.caption || ""} onChange={(e) => updateCaption(idx, e.target.value)} className="absolute bottom-0 left-0 right-0 h-7 text-[10px] rounded-none border-0 bg-black/60 text-white placeholder:text-white/60" />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-[#E8DEC8] p-3 bg-white/50">
              <Label className="text-sm text-[#5C061C]">Call-to-Action button (optional)</Label>
              <p className="text-xs text-[#6E6259] mt-1 mb-2">Shows a button in the post popup. E.g. "Register Now" → /events, or "Donate" → /events, or a full URL.</p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Button label (e.g. Register Now)" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="post-cta-label-input" />
                <Input placeholder="URL or path (e.g. /events)" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} data-testid="post-cta-url-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#6E6259]">Date (optional — defaults to today)</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="post-date-input" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-[#E8DEC8] bg-white px-3 py-2">
              <div>
                <Label className="text-sm text-[#5C061C]">📌 Pin to top of carousel</Label>
                <p className="text-xs text-[#6E6259]">Pinned posts always appear first on the Home page.</p>
              </div>
              <Switch checked={!!form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} data-testid="post-pin-toggle" />
            </div>
            <Button onClick={save} className="btn-saffron w-full rounded-full h-11" data-testid="post-save-button">{form.id ? "Update" : "Publish"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PaymentsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments").then(({ data }) => setItems(data)); }, []);
  return (
    <Card>
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>User</TableHead><TableHead>Item</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Txn</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="text-xs">{new Date(p.created_at).toLocaleString()}</TableCell>
              <TableCell>{p.user_mobile}</TableCell>
              <TableCell>{p.item_name || p.event_title || "—"}</TableCell>
              <TableCell><Badge className="bg-[#5C061C]/10 text-[#5C061C]">{p.type}</Badge></TableCell>
              <TableCell>₹{p.amount}</TableCell>
              <TableCell className="font-mono text-xs">{p.razorpay_txn_id}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
